/**
 * IndexedDB-based asset caching service with ETag validation.
 *
 * **Architecture & Usage Pattern:**
 *
 * This module implements a type-safe caching pattern where callers should use specific,
 * purpose-built helper functions rather than directly calling the low-level `fetchWithCache`.
 * This ensures:
 * - **Type safety**: Each asset type (character model, equipment, item icon) has its own function
 * - **Consistency**: Cache keys, URLs, and Accept headers are computed in one place
 * - **Maintainability**: Adding a new asset type requires one new function, not scattered changes
 *
 * **Available functions for callers:**
 * - `loadCharacterModelBytes(characterModelId)` - Character body 3D models (GLB)
 * - `loadEquipmentModelBytes(itemDefinitionId)` - Equipment/weapon/armor 3D models (GLB)
 * - `loadBodyPartModelBytes(bodyPartId)` - Cosmetic body part 3D models: hair, eyebrows, outfit (GLB)
 * - `loadAnimationBaseBytes(skeletonTypeId)` - Shared skeleton animation base (GLB)
 * - `loadItemIconBytes(itemDefinitionId)` - Item preview PNG images
 *
 * **Internal implementation:**
 * IndexedDB stores separate metadata and cache object stores for each asset type:
 * - `character-metadata` / `character-cache` - Character 3D models
 * - `equipment-metadata` / `equipment-cache` - Equipment 3D models
 * - `item-icon-metadata` / `item-icon-cache` - Item PNG icons
 * - `animation-metadata` / `animation-cache` - Skeleton animation bases
 *
 * Separate stores provide type-safe keys (no prefix needed) and make the schema
 * self-documenting. All requests use ETag-based HTTP caching: If-None-Match headers
 * trigger 304 responses when the asset hasn't changed, avoiding unnecessary re-downloads.
 *
 * Can be used in non-React async contexts. Thread-safe across multiple requests via
 * in-flight request deduplication.
 */
import { createLogger } from '@sorrem/shared';

const logger = createLogger('engine:model-cache');

/**
 * Metadata for a cached asset (model, image, or other binary data).
 */
export interface AssetMetadata {
    /** Unique identifier for the asset */
    id: string;
    /** Human-readable name of the asset */
    name: string;
    /** Content type of the asset (e.g., 'model/gltf-binary', 'image/png') */
    contentType: string;
    /** Timestamp when the asset was first cached (milliseconds since epoch) */
    cachedAt: number;
    /** ETag hash for HTTP cache validation */
    etag: string;
}

/** The cache database's connection, opened once and shared by every caller. */
let opening: Promise<IDBDatabase> | null = null;

export const SORREM_CACHE_DB_NAME = 'sorrem-cache';

export const SORREM_CACHE_DB_VERSION = 1;

/**
 * Converts binary data to a base64-encoded data URI, chunked to avoid
 * call-stack limits on large buffers (multi-MB `.glb` files). Browser-safe - 
 * unlike `Buffer.from(...).toString('base64')`, `Buffer` doesn't exist in
 * the browser. BabylonJS's `SceneLoader.LoadAssetContainerAsync` requires a
 * real `data:` URI for in-memory loading (a `blob:` object URL is not
 * sufficient - it has no filename extension for Babylon to detect the
 * format from).
 */
export function arrayBufferToDataUri(data: ArrayBuffer, mimeType = 'application/octet-stream'): string {
    const bytes = new Uint8Array(data);
    const chunkSize = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return `data:${mimeType};base64,${btoa(binary)}`;
}

/**
 * Opens the IndexedDB database for model caching, creating its object stores
 * on first use. Concurrent first callers share one open (#324): each opening
 * its own leaked every connection but the last. A connection that closes
 * (or fails to open) is forgotten, so the next caller opens a new one.
 */
function openDatabase(): Promise<IDBDatabase> {
    opening ??= new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(SORREM_CACHE_DB_NAME, SORREM_CACHE_DB_VERSION);

        request.onerror = (): void => reject(request.error);
        request.onsuccess = (): void => {
            const database = request.result;
            database.addEventListener('close', () => forgetDatabase());
            resolve(database);
        };

        request.onupgradeneeded = (event): void => {
            const database = (event.target as IDBOpenDBRequest).result;

            // Character models
            if (!database.objectStoreNames.contains('character-metadata')) {
                const characterMetadataStore = database.createObjectStore('character-metadata', { keyPath: 'id' });
                characterMetadataStore.createIndex('name', 'name', { unique: false });
            }
            if (!database.objectStoreNames.contains('character-cache')) {
                database.createObjectStore('character-cache', { keyPath: 'id' });
            }

            // Equipment models
            if (!database.objectStoreNames.contains('equipment-metadata')) {
                const equipmentMetadataStore = database.createObjectStore('equipment-metadata', { keyPath: 'id' });
                equipmentMetadataStore.createIndex('name', 'name', { unique: false });
            }
            if (!database.objectStoreNames.contains('equipment-cache')) {
                database.createObjectStore('equipment-cache', { keyPath: 'id' });
            }

            // Item icons
            if (!database.objectStoreNames.contains('item-icon-metadata')) {
                const itemIconMetadataStore = database.createObjectStore('item-icon-metadata', { keyPath: 'id' });
                itemIconMetadataStore.createIndex('name', 'name', { unique: false });
            }
            if (!database.objectStoreNames.contains('item-icon-cache')) {
                database.createObjectStore('item-icon-cache', { keyPath: 'id' });
            }

            // Animation bases
            if (!database.objectStoreNames.contains('animation-metadata')) {
                const animationMetadataStore = database.createObjectStore('animation-metadata', { keyPath: 'id' });
                animationMetadataStore.createIndex('name', 'name', { unique: false });
            }
            if (!database.objectStoreNames.contains('animation-cache')) {
                database.createObjectStore('animation-cache', { keyPath: 'id' });
            }
        };
    }).catch((error: unknown) => {
        forgetDatabase();
        throw error;
    });
    return opening;
}

function forgetDatabase(): void {
    opening = null;
}

/** A transaction on a connection that is closing throws this DOMException. */
function isConnectionClosing(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'InvalidStateError';
}

/** Runs `use` on the database, opening it again once if its connection was closing. */
async function withDatabase<T>(use: (database: IDBDatabase) => Promise<T>): Promise<T> {
    const database = await openDatabase();
    try {
        return await use(database);
    } catch (error) {
        if (!isConnectionClosing(error)) {
            throw error;
        }
        forgetDatabase();
        return use(await openDatabase());
    }
}

/** Reads one record from a store, or undefined when it is missing or unreadable. */
function readRecord<T>(database: IDBDatabase, storeName: string, id: string): Promise<T | undefined> {
    const store = database.transaction([storeName], 'readonly').objectStore(storeName);
    return new Promise<T | undefined>((resolve) => {
        const request = store.get(id);
        request.onsuccess = (): void => resolve(request.result as T | undefined);
        request.onerror = (): void => resolve(undefined);
    });
}

/** Whether a store has a record, found by its key alone so the record (a model's bytes) is never read. */
function hasRecord(database: IDBDatabase, storeName: string, id: string): Promise<boolean> {
    const store = database.transaction([storeName], 'readonly').objectStore(storeName);
    return new Promise<boolean>((resolve) => {
        const request = store.getKey(id);
        request.onsuccess = (): void => resolve(request.result !== undefined);
        request.onerror = (): void => resolve(false);
    });
}

/** Where downloaded assets are kept between sessions: IndexedDB in the app, a fake in tests. */
export interface AssetByteStore {
    readMetadata(metadataStoreName: string, assetId: string): Promise<AssetMetadata | undefined>;
    readData(cacheStoreName: string, assetId: string): Promise<ArrayBuffer | undefined>;
    /** Whether an asset's bytes are kept, without reading them. */
    hasData(cacheStoreName: string, assetId: string): Promise<boolean>;
    /** Keeps an asset's bytes and metadata together: both are kept or neither is. */
    write(metadataStoreName: string, cacheStoreName: string, metadata: AssetMetadata, data: ArrayBuffer): Promise<void>;
}

const indexedDbAssetStore: AssetByteStore = {
    readMetadata: (metadataStoreName, assetId) => withDatabase((database) => readRecord<AssetMetadata>(database, metadataStoreName, assetId)),
    readData: (cacheStoreName, assetId) => withDatabase(async (database) => (await readRecord<{ data: ArrayBuffer }>(database, cacheStoreName, assetId))?.data),
    hasData: (cacheStoreName, assetId) => withDatabase((database) => hasRecord(database, cacheStoreName, assetId)),
    write: (metadataStoreName, cacheStoreName, metadata, data) => withDatabase((database) => {
        // One transaction: metadata written without its bytes (storage full
        // on a multi-MB model) would send If-None-Match for bytes not there.
        const transaction = database.transaction([cacheStoreName, metadataStoreName], 'readwrite');
        transaction.objectStore(cacheStoreName).put({ id: metadata.id, data });
        transaction.objectStore(metadataStoreName).put(metadata);
        // A failed write only means the asset is fetched again next time;
        // waiting on success alone left the load hanging for good.
        return new Promise<void>((resolve) => {
            transaction.oncomplete = (): void => resolve();
            transaction.onerror = transaction.onabort = (): void => {
                logger.warn(`Could not cache ${metadata.id}:`, transaction.error);
                resolve();
            };
        });
    }),
};

// Deduplicates concurrent requests for the same asset. Multiple UI components
// (item icon in inventory, equipped-gear panel, tooltip, etc.) may independently
// request the same asset on mount. Without deduplication, N concurrent callers
// for the same asset would fire N redundant network fetches after the IndexedDB
// cache completes (none of their writes landed yet). Deduplication ensures only
// one fetch runs for identical modelIds.
const inFlightRequests = new Map<string, Promise<ArrayBuffer>>();

/**
 * Low-level internal caching function. DO NOT CALL DIRECTLY.
 *
 * Use type-specific helpers instead:
 * - loadCharacterModelBytes()
 * - loadEquipmentModelBytes()
 * - loadBodyPartModelBytes()
 * - loadAnimationBaseBytes()
 * - loadItemIconBytes()
 *
 * @internal
 */
const fetchWithCache = (asset: CachedAssetRequest): Promise<ArrayBuffer> => {
    const { assetId, url, assetName, cacheStoreName } = asset;
    if (!assetId) {
        logger.error("fetchWithCache called with empty assetId");
        return Promise.reject(new Error(`fetchWithCache: empty assetId for ${assetName}`));
    }

    // Only the same request shares a fetch: kinds of asset can share an id (an
    // item's model and its icon), and must never be handed each other's bytes.
    const requestKey = `${cacheStoreName}|${url}|${assetId}`;
    const existing = inFlightRequests.get(requestKey);
    if (existing) {
        return existing;
    }

    const request = loadAssetBytes(indexedDbAssetStore, asset).finally(() => {
        inFlightRequests.delete(requestKey);
    });
    inFlightRequests.set(requestKey, request);
    return request;
};

/** Where each kind of cataloged model is served (by id, under `route`), what it is called, and which IndexedDB stores keep it. */
const CATALOGED_MODELS = {
    character: { route: '/assets/characters/', label: 'Character Mesh', metadataStoreName: 'character-metadata', cacheStoreName: 'character-cache' },
    equipment: { route: '/assets/items/', label: 'Equipment Model', metadataStoreName: 'equipment-metadata', cacheStoreName: 'equipment-cache' },
    'body-part': { route: '/assets/body-parts/', label: 'Body Part Model', metadataStoreName: 'equipment-metadata', cacheStoreName: 'equipment-cache' },
    // Routed by skeleton type, not the raw static path: see `loadAnimationBaseBytes`.
    animation: { route: '/assets/skeleton-types/', label: 'Animation Base', metadataStoreName: 'animation-metadata', cacheStoreName: 'animation-cache' },
} as const;

export type CatalogedModelKind = keyof typeof CATALOGED_MODELS;

/** Content hashes from the asset catalogs, keyed `<metadata store>:<asset id>`. */
const knownContentHashes = new Map<string, string>();

/**
 * Records the content hashes the asset catalogs list for their models
 * (`bodyMeshHash`, `modelHash`, `animationBaseFileHash`; each is also the
 * model's served ETag). A model whose cached copy carries the listed hash
 * is then used straight from IndexedDB, with no request to revalidate it.
 * Called by `CharacterLoader.initialize` with the catalogs it loads.
 */
export function registerModelContentHashes(kind: CatalogedModelKind, hashes: Iterable<readonly [assetId: string, hash: string | undefined]>): void {
    const storeName = CATALOGED_MODELS[kind].metadataStoreName;
    for (const [assetId, hash] of hashes) {
        if (hash) {
            knownContentHashes.set(`${storeName}:${assetId}`, hash);
        }
    }
}

/** One asset to fetch through the cache: where it is served, and which stores keep it. */
export interface CachedAssetRequest {
    readonly assetId: string;
    readonly url: string;
    readonly assetName: string;
    readonly contentType: string;
    readonly metadataStoreName: string;
    readonly cacheStoreName: string;
    readonly requestHeaders?: Record<string, string>;
}

/**
 * An asset through the cache: `readKept` when the catalog's content hash
 * vouches for the kept copy or the server says it is current (304), else
 * downloaded, kept, and handed to `fromDownload`. Falls back to `readKept`
 * when the download fails.
 */
async function resolveThroughCache<T>(
    store: AssetByteStore,
    asset: CachedAssetRequest,
    readKept: () => Promise<T | undefined>,
    fromDownload: (data: ArrayBuffer) => T,
): Promise<T> {
    const { assetId, url, metadataStoreName, cacheStoreName } = asset;
    const existingMetadata = await store.readMetadata(metadataStoreName, assetId);

    // The catalog vouches for this copy: same content hash, so skip the round trip.
    const knownHash = knownContentHashes.get(`${metadataStoreName}:${assetId}`);
    if (knownHash !== undefined && existingMetadata?.etag === `"${knownHash}"`) {
        const cachedData = await readKept();
        if (cachedData) {
            return cachedData;
        }
    }

    try {
        const headers: Record<string, string> = { ...asset.requestHeaders };
        if (existingMetadata?.etag) {
            headers['If-None-Match'] = existingMetadata.etag;
        }
        let response = await fetch(url, { headers });

        if (response.status === 304) {
            const cachedData = await readKept();
            if (cachedData) {
                logger.debug(`Cache hit for ${assetId}`);
                return cachedData;
            }
            // Metadata kept without its bytes: a 304 can't supply them, so download them in full.
            response = await fetch(url, { headers: { ...asset.requestHeaders } });
        }

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        logger.debug(`Cache miss/update for ${assetId}`);
        const data = await response.arrayBuffer();
        const etag = response.headers.get('ETag') || crypto.randomUUID();
        await store.write(metadataStoreName, cacheStoreName, { id: assetId, name: asset.assetName, contentType: asset.contentType, cachedAt: Date.now(), etag }, data);
        return fromDownload(data);
    } catch (error) {
        // Fall back to cache if fetch fails
        if (existingMetadata) {
            const cachedData = await readKept();
            if (cachedData) {
                logger.warn(`Fetch failed for ${assetId}, using cached data`);
                return cachedData;
            }
        }
        throw error;
    }
}

/**
 * An asset's bytes: from `store` when the server says they are current
 * (304) or the catalog's content hash matches, else downloaded and kept.
 * Falls back to kept bytes when the download fails. Exported for tests;
 * callers use the type-specific helpers below.
 *
 * @internal
 */
export function loadAssetBytes(store: AssetByteStore, asset: CachedAssetRequest): Promise<ArrayBuffer> {
    return resolveThroughCache(store, asset, () => store.readData(asset.cacheStoreName, asset.assetId), (data) => data);
}

/**
 * Makes sure `store` keeps an asset, the same way `loadAssetBytes` would,
 * but never reads kept bytes back: precaching (#355) only needs them there.
 * Exported for tests; callers use `cacheCatalogedModel`.
 *
 * @internal
 */
export async function cacheAssetBytes(store: AssetByteStore, asset: CachedAssetRequest): Promise<void> {
    await resolveThroughCache(store, asset, async () => (await store.hasData(asset.cacheStoreName, asset.assetId)) || undefined, () => true);
}


/** The request for a cataloged model: where it is served and which stores keep it. */
function catalogedModelRequest(kind: CatalogedModelKind, id: string): CachedAssetRequest {
    const model = CATALOGED_MODELS[kind];
    return {
        assetId: id,
        url: `${model.route}${id}`,
        assetName: `${model.label} (${id})`,
        contentType: 'model/gltf-binary',
        metadataStoreName: model.metadataStoreName,
        cacheStoreName: model.cacheStoreName,
        requestHeaders: { Accept: 'model/gltf-binary' },
    };
}

/**
 * Makes sure a cataloged model is kept in IndexedDB, for the asset precache
 * (#285, #355): downloaded if missing or out of date, never read back.
 */
export function cacheCatalogedModel(kind: CatalogedModelKind, id: string): Promise<void> {
    return cacheAssetBytes(indexedDbAssetStore, catalogedModelRequest(kind, id));
}

/**
 * **PUBLIC HELPERS - Use these instead of fetchWithCache**
 *
 * Type-safe asset fetching functions. Each knows its asset type's URL pattern,
 * cache key format, and content negotiation headers. This ensures consistency
 * and eliminates the bug-prone manual construction of cache keys and Accept headers.
 */

/**
 * Fetch a character body 3D model (GLB).
 *
 * The model is cached in IndexedDB with ETag validation, so repeated requests
 * for the same character use a 304 Not Modified response if the file hasn't changed.
 *
 * @param characterModelId - The character model ID (e.g., 'paladin', 'rogue')
 * @returns Promise resolving to GLB binary data ready for BabylonJS SceneLoader
 * @throws Error if fetch fails and no cache exists
 */
export const loadCharacterModelBytes = (characterModelId: string): Promise<ArrayBuffer> => {
    return fetchWithCache(catalogedModelRequest('character', characterModelId));
};

/**
 * Fetch an equipment (weapon/armor) 3D model (GLB).
 *
 * The model is cached in IndexedDB with ETag validation. Multiple equipped items
 * are cached separately, allowing live re-equipping without re-fetching unchanged models.
 *
 * @param itemDefinitionId - The item definition ID
 * @returns Promise resolving to GLB binary data ready for BabylonJS SceneLoader
 * @throws Error if fetch fails and no cache exists
 */
export const loadEquipmentModelBytes = (itemDefinitionId: string): Promise<ArrayBuffer> => {
    return fetchWithCache(catalogedModelRequest('equipment', itemDefinitionId));
};

/**
 * Fetch a body part (cosmetic) 3D model (GLB) - hair, eyebrows, outfit pieces.
 *
 * The model is cached in IndexedDB with ETag validation. Multiple body parts
 * are cached separately, allowing fast switching between outfit combinations.
 *
 * @param bodyPartId - The body part definition ID
 * @returns Promise resolving to GLB binary data ready for BabylonJS SceneLoader
 * @throws Error if fetch fails and no cache exists
 */
export const loadBodyPartModelBytes = (bodyPartId: string): Promise<ArrayBuffer> => {
    return fetchWithCache(catalogedModelRequest('body-part', bodyPartId));
};

/**
 * Fetch a held-item 3D model (GLB) that has no catalog entry of its own -
 * e.g. the crystal a character holds while using it. Served from the static
 * `/assets` mount by its path under `game-content/assets/`, and cached
 * alongside equipment models under that path.
 *
 * @param modelPath - The model's path under `game-content/assets/`
 * @returns Promise resolving to GLB binary data ready for BabylonJS SceneLoader
 * @throws Error if fetch fails and no cache exists
 */
export const loadHeldItemModelBytes = (modelPath: string): Promise<ArrayBuffer> => {
    return fetchWithCache({
        assetId: modelPath,
        url: `/assets/${modelPath}`,
        assetName: `Held Item Model (${modelPath})`,
        contentType: 'model/gltf-binary',
        metadataStoreName: 'equipment-metadata',
        cacheStoreName: 'equipment-cache',
        requestHeaders: { Accept: 'model/gltf-binary' },
    });
};

/**
 * Fetch one texture of a character model's texture variant (an elite's
 * coloring; see `CharacterModel.textureVariants`). Served from the static
 * `/assets` mount by its path under `game-content/assets/`, and cached
 * alongside character models under that path.
 *
 * @param texturePath - The texture's path under `game-content/assets/`
 * @returns Promise resolving to the image's bytes
 * @throws Error if fetch fails and no cache exists
 */
export const loadCharacterTextureBytes = (texturePath: string): Promise<ArrayBuffer> => {
    return fetchWithCache({
        assetId: texturePath,
        url: `/assets/${texturePath}`,
        assetName: `Character Texture (${texturePath})`,
        contentType: 'image/webp',
        metadataStoreName: 'character-metadata',
        cacheStoreName: 'character-cache',
        requestHeaders: { Accept: 'image/webp' },
    });
};

/**
 * Fetch an item's preview PNG icon.
 *
 * Icons are cached separately from 3D models (different stores). This allows
 * the same item ID to have both a 3D model and a 2D preview icon cached without
 * interference.
 *
 * @param itemDefinitionId - The item definition ID
 * @returns Promise resolving to PNG binary data ready for conversion to object URL or data URI
 * @throws Error if fetch fails and no cache exists
 */
export const loadItemIconBytes = (itemDefinitionId: string): Promise<ArrayBuffer> => {
    return fetchWithCache({
        assetId: itemDefinitionId,
        url: `/assets/items/${itemDefinitionId}`,
        assetName: `Item Icon (${itemDefinitionId})`,
        contentType: 'image/png',
        metadataStoreName: 'item-icon-metadata',
        cacheStoreName: 'item-icon-cache',
        requestHeaders: { Accept: 'image/png' },
    });
};

/**
 * Fetch animation base for a skeleton type.
 *
 * The animation base (quaternius_humanoid.glb, etc.) is shared across all characters of the same
 * skeleton type. It's cached by skeleton type ID, not character ID, to avoid duplication.
 *
 * Routed through /assets/skeleton-types/:id (content-negotiated on Accept)
 * rather than the raw static path, so the server can serve this with its
 * pre-generated content hash as the ETag -- stable across deploys, unlike
 * the static mount's mtime-based ETag, which resets (and so falsely
 * invalidates every client's cache) on every container rebuild.
 *
 * @param skeletonTypeId - The skeleton type ID (e.g., 'humanoid')
 * @returns Promise resolving to GLB binary data ready for BabylonJS SceneLoader
 * @throws Error if fetch fails and no cache exists
 */
export const loadAnimationBaseBytes = (
    skeletonTypeId: string,
): Promise<ArrayBuffer> => {
    return fetchWithCache(catalogedModelRequest('animation', skeletonTypeId));
};
